import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";

import type { Operation } from "@workspace/operations/types";
import type {
	MenuCategory,
	OptionGroup,
} from "@workspace/product-catalog-types";
import { useVenueSettings } from "@workspace/venue-management";
import type { StaffMember } from "@workspace/venue-management-staff";
import { useStaffPortalContext } from "@workspace/venue-management-staff";

import { eposApi } from "../../api";
import { useKioskContext } from "../../kiosk";
import {
	defaultCourseBadgeColours,
	type EposCourse,
	type EposData,
	type Order,
	type OrderItem,
	type TabMembershipResponse,
} from "../../types";
import { deactivateStaffToken } from "../staff-token-store";
import { useSendWarnings } from "../use-send-warnings";
import { mergeTabMembership, optimisticTab } from "./merge-tab-membership";
import { optionGroupsByProduct } from "./option-product-map";
import {
	applyReceiptOperations,
	trackOperator,
	type OperatorOperations,
} from "./receipt-operations";
import type { CourseProgress, EposTab } from "./types";
import type { CardCancelResult } from "./use-card-payment";
import { useCourseFire } from "./use-course-fire";
import { useOrders } from "./use-orders";

function computeCourseProgress(
	order: Order | null,
	standardCourses: EposCourse[],
): CourseProgress | null {
	if (!order) return null;

	const orderCourseIds = new Set(
		order.items.map((i) => i.courseId).filter((id): id is string => id != null),
	);

	// Skip empty courses — only consider standard courses that have items on this order
	const standardWithItems = standardCourses.filter((c) =>
		orderCourseIds.has(c.id),
	);

	if (standardWithItems.length === 0) {
		return {
			currentCourse: null,
			nextCourse: null,
			allSent: false,
			hasStandardCourses: false,
		};
	}

	const firedCourseIds = new Set(order.firedCourseIds ?? []);
	const fired = standardWithItems.filter((c) => firedCourseIds.has(c.id));
	const unfired = standardWithItems.filter((c) => !firedCourseIds.has(c.id));

	return {
		currentCourse: fired.length > 0 ? fired[fired.length - 1]!.name : null,
		nextCourse: unfired.length > 0 ? unfired[0]!.name : null,
		allSent: unfired.length === 0,
		hasStandardCourses: true,
	};
}

const PRINT_FAILURE_SEEN_CAP = 200;

function seenStorageKey(venueId: string) {
	return `epos-print-failures-seen:${venueId}`;
}

function loadSeenJobIds(venueId: string): Set<string> {
	try {
		const raw = sessionStorage.getItem(seenStorageKey(venueId));
		return new Set(raw ? (JSON.parse(raw) as string[]) : []);
	} catch {
		return new Set();
	}
}

function persistSeenJobIds(venueId: string, ids: Set<string>) {
	try {
		const arr = Array.from(ids).slice(-PRINT_FAILURE_SEEN_CAP);
		sessionStorage.setItem(seenStorageKey(venueId), JSON.stringify(arr));
	} catch {
		// sessionStorage may be unavailable (private mode, quota); silently skip.
	}
}

function useRecentPrintFailureToasts(
	venueId: string,
	failures: EposData["recentPrintFailures"],
) {
	useEffect(() => {
		if (!failures?.length) return;

		const seen = loadSeenJobIds(venueId);
		let dirty = false;

		for (const failure of failures) {
			if (seen.has(failure.jobId)) continue;
			seen.add(failure.jobId);
			dirty = true;

			if (failure.reasonCode === "NO_DEVICE_ALLOCATED") {
				toast.error(
					"Print failed: no printer device set up for this venue. Contact your manager.",
				);
			} else if (failure.reasonCode === "MALFORMED_HOSTNAME") {
				toast.error(
					"Print failed: printer device is misconfigured. Contact your manager.",
				);
			}
		}

		if (dirty) persistSeenJobIds(venueId, seen);
	}, [venueId, failures]);
}

const CANCEL_REFUSED: Record<
	Exclude<CardCancelResult, { cancelled: true }>["reason"],
	(message?: string) => string
> = {
	processing: () =>
		"The reader is already processing the card. Wait for it to finish.",
	reader: () =>
		"The reader didn't confirm the cancel, so it may still take the card. Check the reader.",
	taken: (message) =>
		message ??
		"The payment has already been taken. Check the order's payments.",
	server: (message) =>
		`${message ?? "The cancel couldn't be sent"}. Try again before taking another payment.`,
};

export const emptyEposData: EposData = {
	settings: {
		receiptFooter: "",
		receiptHeader: "",
		taxRate: 0,
		menus: [],
		products: [],
		courses: [],
		productOptions: {
			optionGroups: [],
			productOptionAssignments: [],
		},
		enabled: false,
	},
	activeTabs: [],
	orders: [],
	activeShifts: [],
	currentCashDrawer: {
		id: "",
		transactions: [],
	},
	salesSummary: {
		thisMonth: { comparedToLastMonth: 0, orders: 0, total: 0 },
		thisWeek: { comparedToLastWeek: 0, orders: 0, total: 0 },
		today: { averageOrder: 0, orders: 0, topSellingItems: [], total: 0 },
	},
	terminals: [],
	courseBadgeColours: undefined,
	recentPrintFailures: [],
	tipsEnabled: false,
};

export function useEposDisplay() {
	const {
		staffMembers,
		setCurrentUserId,
		currentUserId,
		venueId,
		tenant,
		currentUserOperations,
		isLoadingUserOperations,
		deviceId,
	} = useKioskContext();

	const { currency, timeZone } = useVenueSettings();

	const eposQuery = useSuspenseQuery({
		...eposApi.getEposOptions(tenant, venueId),
		refetchInterval: 10_000,
	});

	const currencies = currency;
	const defaultCurrency = currencies.primaryCurrency;
	const defaultCurrencyCode = defaultCurrency.code;

	const { todayShift } = useStaffPortalContext();

	const { data } = eposQuery;

	useRecentPrintFailureToasts(venueId, data.recentPrintFailures);

	const activeMenus = data.settings.menus
		.filter((menu) => menu.isActive)
		.sort((a, b) => (a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1));

	const defaultMenus = activeMenus.filter((menu) => menu.isDefault);

	const [currentMenu, setCurrentMenu] = useState(
		defaultMenus[0] || activeMenus[0] || null,
	);

	const [view, setViewState] = useState<
		| "products"
		| "payment"
		| "settings"
		| "staff"
		| "refund"
		| "refund-history"
		| "orders"
	>("staff");

	async function setView(
		s:
			| "products"
			| "payment"
			| "settings"
			| "staff"
			| "refund"
			| "refund-history"
			| "orders",
	) {
		// HA-1023 — the view changes first and the refetch runs behind it. Awaiting the refetch let
		// two calls resolve out of order, and a slow one held the screen on the view it was leaving.
		setViewState(s);
		await eposQuery.refetch();
	}

	const [selectedCategory, setSelectedCategory] = useState<MenuCategory | null>(
		null,
	);

	const shifts = todayShift;

	const selectedStaff =
		staffMembers.find((staff) => staff.registeredUserId === currentUserId) ||
		null;

	const [activeTabId, setActiveTabId] = useState<string | null>(null);

	// HA-554 — a tab holds several orders; the server's ActiveTabs carry each tab's order ids and figures, and the till
	// shows those. A tab leaves the panel when the server's ActiveTabs no longer have it.
	const tabs = useMemo<EposTab[]>(() => {
		const standardCourses = data.settings.courses
			.filter((c) => c.status === "Active" && !c.autoFire)
			.sort((a, b) => a.sortOrder - b.sortOrder);
		const zero = {
			value: 0,
			currency: defaultCurrencyCode,
			version: 2 as const,
		};

		return data.activeTabs.map((t) => {
			const tabOrders = t.orders
				.map((id) => data.orders.find((o) => o.id === id))
				.filter((o): o is Order => !!o);
			const openOrders = tabOrders.filter((o) => o.status === "Open");
			const itemCount = openOrders.reduce((sum, o) => sum + o.items.length, 0);
			// The newest open order's course progress (each order fires its own courses).
			const latestOpen = openOrders[openOrders.length - 1] ?? null;

			return {
				id: t.id,
				number: t.number,
				name: t.name,
				section: t.section,
				covers: t.covers,
				status: t.status,
				notes: t.notes ?? null,
				orders: tabOrders,
				createdByUserId: t.createdByUserId ?? null,
				updatedAt: t.updatedAt ?? null,
				itemCount,
				total: t.total ?? zero,
				outstanding: t.outstanding ?? zero,
				courseProgress: computeCourseProgress(latestOpen, standardCourses),
			};
		});
	}, [
		data.activeTabs,
		data.orders,
		data.settings.courses,
		defaultCurrencyCode,
	]);

	const queryClient = useQueryClient();
	const eposQueryKey = [tenant, venueId, "epos"];

	const createTabMutation = useMutation({
		...eposApi.createTabOptions(tenant, venueId),
		onMutate(tab) {
			const previous = queryClient.getQueryData<EposData>(eposQueryKey);
			queryClient.setQueryData<EposData>(eposQueryKey, (old) => {
				if (!old) return old;

				// HA-554: a tab with no orders yet (the panel reads its orders), typed, so a missing field can't hide.
				return {
					...old,
					activeTabs: [
						...old.activeTabs,
						optimisticTab(tab, currentUserId ?? null, new Date().toISOString()),
					],
				};
			});
			return { previous };
		},
		onError(_error, _variables, context) {
			if (context?.previous) {
				queryClient.setQueryData<EposData>(eposQueryKey, context.previous);
			}
		},
		onSettled() {
			queryClient.invalidateQueries({ queryKey: eposQueryKey });
		},
	});

	const updateTabMutation = useMutation({
		...eposApi.updateTabOptions(tenant, venueId),
		onMutate({ tabId, tab: update }) {
			const previous = queryClient.getQueryData<EposData>(eposQueryKey);
			queryClient.setQueryData<EposData>(eposQueryKey, (old) => {
				if (!old) return old;
				return {
					...old,
					activeTabs: old.activeTabs.map((t) =>
						t.id === tabId
							? { ...t, name: update.name, notes: update.notes }
							: t,
					),
				};
			});
			return { previous };
		},
		onError(_error, _variables, context) {
			if (context?.previous) {
				queryClient.setQueryData<EposData>(eposQueryKey, context.previous);
			}
		},
	});

	// HA-554 (planning's ruling on #415): the answer is the order and each tab it touched. Merged into the EPOS data in
	// place (menus and products stay), the touched tabs' details cached, and EPOS refreshed in the background.
	const applyTabMembership = (response: TabMembershipResponse) => {
		queryClient.setQueryData<EposData>(eposQueryKey, (old) =>
			old ? mergeTabMembership(old, response) : old,
		);
		for (const detail of response.tabs) {
			queryClient.setQueryData(
				eposApi.getTabOptions(tenant, venueId, detail.tab.id).queryKey,
				detail,
			);
		}
		void queryClient.invalidateQueries({ queryKey: eposQueryKey, exact: true });
	};

	const assignOrderToTabMutation = useMutation({
		...eposApi.assignOrderToTabOptions(tenant, venueId),
		onSuccess(result) {
			if (!result.success) return;
			applyTabMembership(result.response);
		},
	});

	const moveOrderToTabMutation = useMutation({
		...eposApi.moveOrderToTabOptions(tenant, venueId),
		onSuccess(result) {
			if (!result.success) return;
			applyTabMembership(result.response);
		},
	});

	const [isOpenDrawerDialogOpen, setIsOpenDrawerDialogOpen] = useState(false);

	const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false);

	// Refund state - stores the order being refunded
	const [refundOrder, setRefundOrder] = useState<Order | null>(null);

	const openCashDrawerMutation = useMutation({
		...eposApi.openCashDrawerWithoutSaleOptions(tenant, venueId),
		onSuccess(result) {
			if (!result.success) return;
			queryClient.setQueryData([tenant, venueId, "epos"], result.response);
			toast.success("Cash drawer opened");
			setIsOpenDrawerDialogOpen(false);
		},
		onError(error) {
			toast.error("Failed to open cash drawer", {
				description: error instanceof Error ? error.message : "Unknown error",
			});
		},
	});

	const { mutateAsync: openCashDrawer, isPending: isOpeningCashDrawer } =
		openCashDrawerMutation;

	const [cashDrawer, setCashDrawer] = useState(data.currentCashDrawer);
	const [activeShifts, setActiveShifts] = useState(data.activeShifts);
	const [salesSummary, setSalesSummary] = useState(data.salesSummary);

	const orders = useOrders({
		selectedStaff,
		settings: data.settings,
		setView,
		tabs,
		defaultCurrency,
		defaultCurrencyCode,
		data,
		setActiveTabId,
		setSelectedCategory,
		venueId,
		zero: currencies.money(0),
		tenant,
		setCurrentUserId,
		deviceId,
	});

	// HA-619 — one presenter for every send path, so a course fire and the Send button warn alike.
	const sendWarnings = useSendWarnings();

	const courseFire = useCourseFire({
		tenant,
		venueId,
		deviceId,
		currentOrder: orders.currentOrder ?? null,
		onUpdateOrder: orders.updateOrder,
		onSendWarnings: sendWarnings.report,
		onOptionRefusal: orders.showOptionRefusal,
	});

	// HA-767 — Cancel leaves the payment screen only once the payment really is cancelled (HA-139
	// AC3). When it is not, the screen stays and says why: a screen that said "cancelled" over a card
	// about to be charged is how money was taken with no completed order.
	async function cancelPayment() {
		const result = await orders.clearPayments();

		if (!result.cancelled) {
			toast.error("This card payment can't be cancelled", {
				description: CANCEL_REFUSED[result.reason](result.message),
			});
			return;
		}

		await setView("products");
	}

	const { settings } = data;

	const optionProductMap = optionGroupsByProduct(
		settings.productOptions.productOptionAssignments,
		settings.productOptions.optionGroups,
	);

	const getAvailableOptions = React.useCallback(
		(item: OrderItem): OptionGroup[] => {
			if (!optionProductMap[item.productId]) {
				return [];
			}

			const groups = optionProductMap[item.productId];

			return groups || [];
		},
		[optionProductMap],
	);

	// HA-360 (planning ruling) — the receipt dialog's Print Receipt gate keeps the completing operator's operations while the
	// modal is open; see receipt-operations.ts. It never feeds the general currentUserOperations above.
	const operatorRef = useRef<OperatorOperations>({
		operatorId: null,
		operations: [],
	});
	const [receiptOperations, setReceiptOperations] = useState<
		Operation[] | null
	>(null);

	useEffect(() => {
		operatorRef.current = trackOperator(
			operatorRef.current,
			currentUserId,
			currentUserOperations,
		);
	}, [currentUserId, currentUserOperations]);

	useEffect(() => {
		setReceiptOperations(
			applyReceiptOperations(
				orders.isReceiptModalOpen
					? {
							type: "modalOpened",
							operatorOperations: operatorRef.current.operations,
						}
					: { type: "modalClosed" },
			),
		);
	}, [orders.isReceiptModalOpen]);

	useEffect(() => {
		if (!currentUserId) return;
		// eslint-disable-next-line react-hooks/set-state-in-effect -- HA-360: selecting a staff member clears the snapshot
		setReceiptOperations(applyReceiptOperations({ type: "staffSelected" }));
	}, [currentUserId]);

	return {
		...orders,
		receiptOperations,
		createTab: createTabMutation.mutateAsync,
		isCreatingTab: createTabMutation.isPending,
		updateTab: updateTabMutation.mutateAsync,
		isUpdatingTab: updateTabMutation.isPending,
		assignOrderToTab: assignOrderToTabMutation.mutateAsync,
		isAssigningOrderToTab: assignOrderToTabMutation.isPending,
		moveOrderToTab: moveOrderToTabMutation.mutateAsync,
		isMovingOrder: moveOrderToTabMutation.isPending,
		cancelPayment,
		setCurrentUserId,

		defaultCurrencyCode,

		data,
		settings: data.settings,
		courseBadgeColours: data.courseBadgeColours ?? defaultCourseBadgeColours,

		view,
		setView,

		selectedCategory,
		setSelectedCategory,

		products: data.settings.products,
		getAvailableOptions,

		activeMenus,
		currentMenu,
		defaultMenus,
		setCurrentMenu,

		staffMembers,
		shifts,
		selectedStaff,
		setSelectedStaff: (staffMember: StaffMember | null) => {
			if (!staffMember) {
				// HA-831: switching away ends the staff session at the server.
				void deactivateStaffToken();
			}
			setCurrentUserId(staffMember?.registeredUserId);
		},
		tabs,
		activeTabId,
		setActiveTabId,

		cashDrawer,
		setCashDrawer,
		activeShifts,
		setActiveShifts,
		salesSummary,
		setSalesSummary,
		currentUserId,
		venueId,
		tenant,

		currencies,
		timeZone,

		openCashDrawer,
		isOpeningCashDrawer,
		isOpenDrawerDialogOpen,
		setIsOpenDrawerDialogOpen,

		refundOrder,
		setRefundOrder,
		isRefundDialogOpen,
		setIsRefundDialogOpen,

		operations: currentUserOperations,
		isLoadingUserOperations,

		courseFire,
		sendWarnings,
	};
}
